import React from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { Button } from "./ui/button";
import { Smartphone, Monitor, Trash2 } from "lucide-react";

function fmt(d) {
  if (!d) return "—";
  try {
    return new Date(d).toLocaleString("en-ZA", { dateStyle: "medium", timeStyle: "short" });
  } catch {
    return d;
  }
}

/**
 * Shown when login returns HTTP 409 "device_limit_reached". User must revoke
 * one of the two existing devices before they can sign in on this new one.
 *
 * Props:
 *   open               (bool)
 *   onClose            ()
 *   devices            ([{device_id, label, first_seen, last_seen}])
 *   onRevoke           async (device_id) → void   (caller re-tries login afterwards)
 *   loadingId          (string|null)  device_id currently being revoked
 */
export default function DeviceLimitModal({ open, onClose, devices, onRevoke, loadingId }) {
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose?.()}>
      <DialogContent className="max-w-md" data-testid="device-limit-modal">
        <DialogHeader>
          <DialogTitle>Device limit reached</DialogTitle>
        </DialogHeader>
        <p className="text-sm text-slate-600">
          Your RapidCare SA account is currently used on <strong>{(devices || []).length} devices</strong> — the maximum allowed per single licence to prevent account sharing.
        </p>
        <p className="text-sm text-slate-600">
          To sign in on this device, please remove one of the devices below. The removed device will be signed out immediately.
        </p>
        <div className="mt-3 space-y-2">
          {(devices || []).map((d) => (
            <div
              key={d.device_id}
              className="flex items-start justify-between p-3 border border-slate-200 rounded-md bg-white"
              data-testid={`device-row-${d.device_id}`}
            >
              <div className="flex items-start gap-3">
                {(d.label || "").toLowerCase().match(/phone|android|ios|iphone|ipad/) ? (
                  <Smartphone className="w-5 h-5 text-slate-400 mt-0.5" />
                ) : (
                  <Monitor className="w-5 h-5 text-slate-400 mt-0.5" />
                )}
                <div>
                  <div className="font-medium text-sm text-[#111827]">{d.label || "Unknown device"}</div>
                  <div className="text-[11px] text-slate-500 mt-0.5">Added {fmt(d.first_seen)} · last used {fmt(d.last_seen)}</div>
                </div>
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={() => onRevoke?.(d.device_id)}
                disabled={loadingId === d.device_id}
                className="text-rose-700 border-rose-200 hover:bg-rose-50"
                data-testid={`device-revoke-${d.device_id}`}
              >
                <Trash2 className="w-3.5 h-3.5 mr-1" /> {loadingId === d.device_id ? "Removing..." : "Remove"}
              </Button>
            </div>
          ))}
        </div>
        <Button variant="outline" onClick={onClose} className="mt-3 w-full" data-testid="device-limit-cancel">
          Cancel
        </Button>
      </DialogContent>
    </Dialog>
  );
}
